import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MessageService, TreeNode } from 'primeng/api';
import { UserListLookup } from '../../../app/shared/models/users/user-list-lookup';
import { DocumentService } from '../services/document.service';
import { SharedConstants } from '../../../app/shared/constants/shared-constants';
import { UserReplacementDetails } from '../models/user-replacement-details';
import { DocumentPersistenceService } from '../services/document-persistence.service';
import { DocumentVersionDetailsForBulkUserReplacement } from '../models/document-version-details-for-bulk-user-replacement';
import { DefaultValueConstants } from '../../../app/shared/constants/default-value-constants';
import { AppliedFilters } from '../../shared-components/panel-hub/models/applied-filters';
import { PaginationState } from '../../shared-components/panel-hub/models/pagination-state';
import { SortCriteria } from '../../shared-components/panel-hub/models/sort-criteria';

@Component({
  selector: 'app-document-bulk-user-replacement',
  templateUrl: './document-bulk-user-replacement.component.html',
  styleUrls: ['./document-bulk-user-replacement.component.scss']
})

export class DocumentBulkUserReplacementComponent implements OnInit {
  @Output() isBulkUserReplacementSliderActive = new EventEmitter<boolean>();
  @Output() clearSelectionsRequested = new EventEmitter<void>();
  @Input() isBulkUserReplacementSliderOpen: boolean = false;
  
  // Inputs for search state (for select-all mode)
  @Input() currentFilters: AppliedFilters | null = null;
  @Input() currentSort: SortCriteria | null = null;
  @Input() currentPagination: PaginationState | null = null;
  @Input() primarySearchText: string | null = null;
  @Input() secondarySearchText: string | null = null;

  // Inputs for selection state from panel-hub
  @Input() selectAllActive: boolean = false;
  @Input() selectedDocumentIds: Set<string> | null = null;
  @Input() deselectedDocumentIds: Set<string> | null = null;
  @Input() totalRecordCount: number = 0;

  mandatoryFields = {
    assignments: false,
    usersToAdd : false 
  };
  terminateUserList : UserListLookup[] = [];
  activeUsersList : UserListLookup[] = [];
  usersToAddList: UserListLookup[] = [];
  assignmentList: string[] = [];
  topBarTitle: string = SharedConstants.BulkUserReplacement_Title;
  valueField: string = SharedConstants.ValueField;
  labelField: string = SharedConstants.NameField;
  selectedAssignments: any = [];
  treeData: TreeNode[] = [];
  selectedUserToRemove :any = null;
  selectedUserToAdd : string = "";
  validInput : boolean = false;
  createBulkUserReplacementRequest: any;
  userReplacementDetails: UserReplacementDetails = new UserReplacementDetails();
  terminatedUserSelected : boolean = false;
  activeUserSelected : boolean = false;


  constructor(private documentService: DocumentService, private documentPersistenceService: DocumentPersistenceService
  ) { }

  ngOnInit(): void {
    this.initialize();

    this.documentPersistenceService.closeAndResetBulkUserRequirementSlider$.subscribe((isClose) => {
      if (isClose) { this.closeBulkUserRequirementUpdateSlider(); }
    });
  }

  initialize() : void {
    this.documentService.getBulkUserReplacementLookup()
      .subscribe(
      {
        next: (response: any) => {
          this.usersToAddList = response.activeUsersList;
          this.terminateUserList = response.terminatedUsersList;
          this.activeUsersList = response.activeUsersList;
          this.assignmentList = response.assignments;
          this.treeData = this.createUserDetailsTreeNodes(this.terminateUserList, this.activeUsersList);
        },
        error: (error) => {
          console.error('Error fetching Bulk User Requirement lookup: ', error); // Handle error
        }
      }
      );
  }

  closeConfirmation(){
    this.documentPersistenceService.closeConfirmationDialogForBulkUpdate();
  }

  closeBulkUserRequirementUpdateSlider(){
    this.isBulkUserReplacementSliderOpen = false;
    this.resetSlider();
    // Clear all selections after bulk user replacement
    this.clearSelectionsRequested.emit();
    this.isBulkUserReplacementSliderActive.emit(false);
  }

  resetSlider = () => {
    // Only reset the local form state, NOT the @Input values (which come from parent)
    // This allows the parent's selection state to be preserved
    this.selectedAssignments = [];
    this.selectedUserToRemove = "";
    this.selectedUserToAdd = "";
    this.validInput = false;
    // DO NOT reset selectAllActive, selectedDocumentIds, deselectedDocumentIds
    // These are @Input values from the parent and should be preserved
  }

  updateUserRequirement = () => { 
    this.validateUserInput();
    if (this.validInput) { 
      this.createBulkUserRequirementData();
      const noOfDocuments = this.calculateDocumentCount();
      this.documentPersistenceService.UpdateBulkUserRequirementForDocument(noOfDocuments, this.userReplacementDetails);
    }
  }

  private calculateDocumentCount(): number {
    if (this.selectAllActive) {
      // Select-all mode: totalRecords - deselectedCount
      const deselectedCount = this.deselectedDocumentIds ? this.deselectedDocumentIds.size : 0;
      return this.totalRecordCount - deselectedCount;
    } else {
      // Normal mode: selectedCount
      return this.selectedDocumentIds ? this.selectedDocumentIds.size : 0;
    }
  }

  createBulkUserRequirementData() {
    this.userReplacementDetails.assignments = this.selectedAssignments;
    this.userReplacementDetails.userToAdd = this.selectedUserToAdd;
    if (this.selectedUserToRemove != null) {
      this.userReplacementDetails.userToRemove = this.selectedUserToRemove.label;
    }

    if (this.selectAllActive) {
      // MODE: SELECT ALL
      this.userReplacementDetails.isSelectAcrossAllPages = true;
      this.userReplacementDetails.currentFilters = this.currentFilters;
      this.userReplacementDetails.currentSort = this.currentSort;
      this.userReplacementDetails.currentPagination = this.currentPagination;
      this.userReplacementDetails.primarySearchText = this.primarySearchText;
      this.userReplacementDetails.secondarySearchText = this.secondarySearchText;

      this.userReplacementDetails.deselectedDocumentNumberAndVersion = 
        (this.deselectedDocumentIds ? Array.from(this.deselectedDocumentIds) : []).map(id => 
          this.parseDocumentIdentifier(id)
        );
      this.userReplacementDetails.documentNumberAndVersion = undefined;
    } else {
      this.userReplacementDetails.isSelectAcrossAllPages = false;
      this.userReplacementDetails.currentPagination = this.currentPagination;

      this.userReplacementDetails.documentNumberAndVersion = 
        (this.selectedDocumentIds ? Array.from(this.selectedDocumentIds) : []).map(id => 
          this.parseDocumentIdentifier(id)
        );
      // Clear search criteria
      this.userReplacementDetails.currentFilters = null;
      this.userReplacementDetails.currentSort = null;
      this.userReplacementDetails.currentPagination = null;
      this.userReplacementDetails.primarySearchText = null;
      this.userReplacementDetails.secondarySearchText = null;
      this.userReplacementDetails.deselectedDocumentNumberAndVersion = undefined;
    }
  }

  private parseDocumentIdentifier(id: string): DocumentVersionDetailsForBulkUserReplacement {
    const versionMatch = id.match(/-[Vv](\d+)$/);
    
    if (versionMatch) {
      const documentVersion = parseInt(versionMatch[1], 10);
      const documentNumber = id.substring(0, id.length - versionMatch[0].length);
      return new DocumentVersionDetailsForBulkUserReplacement(documentNumber, documentVersion);
    } else {
      console.warn(`Document ID ${id} has no version suffix, defaulting to version 1`);
      return new DocumentVersionDetailsForBulkUserReplacement(id, 1);
    }
  }

  validateUserInput = () => {
    if (this.selectedAssignments.length === 0 || this.selectedUserToAdd === null || this.selectedUserToAdd.length === 0){
      this.validInput = false;
    }
    else {
      this.validInput = true;
    }
  }

  ngOnDestroy = () => {
    this.documentPersistenceService.closeAndResetBulkUserRequirementSlider$.unsubscribe();
  }

  handleTreeSelectChange(event: any) {
    if (event && event.node) {
      // Ensure the selected node is assigned correctly
      this.selectedUserToRemove = event.node;
  
      // Check if the selected node belongs to terminated users
      if (event.node.parent?.label === DefaultValueConstants.terminatedUsers_label) {
        this.terminatedUserSelected = true;
        this.activeUserSelected = false;
      } else {
        this.terminatedUserSelected = false;
        this.activeUserSelected = true;
      }
  
      this.validateUserInput();
    } else {
      this.selectedUserToRemove = null;
    }
  }
  

  createUserDetailsTreeNodes(terminatedUsersList : UserListLookup[], activeUsersList : UserListLookup[] ): TreeNode[] {
        let terminatedUsersNode: TreeNode = {
          label: DefaultValueConstants.terminatedUsers_label,
          selectable: false,
          children: [],
        };
    
        let activeUsersNode: TreeNode = {
          label: DefaultValueConstants.activeUsers_label,
          selectable: false,
          children: [],
        };
        terminatedUsersList.forEach(user => {
          let nodeLabel = user.name;
          terminatedUsersNode.children!.push({ label: nodeLabel }); 
          })
          activeUsersList.forEach(user => {
            let nodeLabel = user.name;
            activeUsersNode.children!.push({ label: nodeLabel }); 
            })
        return [terminatedUsersNode, activeUsersNode];
      }
}
